import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Filter, X } from "lucide-react";
import { publicApi, resolveImageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../components/ui/sheet";

const CategoryFilters = ({ categories, selectedCategory, handleCategoryChange }) => (
    <div className="space-y-2">
        <button
            onClick={() => handleCategoryChange(null)}
            className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                !selectedCategory 
                    ? "bg-primary/10 text-primary border-l-2 border-primary" 
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
            }`}
            data-testid="filter-all"
        >
            All Equipment
        </button>
        {categories.map((cat) => (
            <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                    selectedCategory === cat.slug 
                        ? "bg-primary/10 text-primary border-l-2 border-primary" 
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
                data-testid={`filter-${cat.slug}`}
            >
                {cat.name}
            </button>
        ))}
    </div>
);

const ProductsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterOpen, setFilterOpen] = useState(false);
    
    const selectedCategory = searchParams.get("category");
    
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [productsRes, categoriesRes] = await Promise.all([
                    publicApi.getProducts({ category: selectedCategory }),
                    publicApi.getCategories()
                ]);
                setProducts(productsRes.data);
                setCategories(categoriesRes.data);
            } catch (err) {
                console.error("Failed to fetch products:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [selectedCategory]);
    
    const handleCategoryChange = useCallback((slug) => {
        if (slug) {
            setSearchParams({ category: slug });
        } else {
            setSearchParams({});
        }
        setFilterOpen(false);
    }, [setSearchParams]);
    
    const currentCategory = categories.find(c => c.slug === selectedCategory);
    
    return (
        <div className="noise-overlay min-h-screen">
            {/* Header */}
            <section className="py-12 lg:py-16 border-b border-border">
                <div className="container mx-auto px-6 lg:px-12">
                    <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-2">Equipment Catalog</p>
                    <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase">
                        {currentCategory ? currentCategory.name : "All Equipment"}
                    </h1>
                    {currentCategory && (
                        <p className="text-muted-foreground mt-4 max-w-2xl">{currentCategory.description}</p>
                    )}
                </div>
            </section>
            
            <div className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="flex gap-8">
                    {/* Desktop Sidebar */}
                    <aside className="hidden lg:block w-64 flex-shrink-0">
                        <div className="sticky top-24">
                            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-4">Categories</p>
                            <CategoryFilters 
                                categories={categories}
                                selectedCategory={selectedCategory}
                                handleCategoryChange={handleCategoryChange}
                            />
                            
                            <div className="mt-8 pt-8 border-t border-border">
                                <Link to="/compare">
                                    <Button variant="outline" className="w-full rounded-none uppercase font-bold tracking-wider">
                                        Compare Systems
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </aside>
                    
                    {/* Main Content */}
                    <div className="flex-1">
                        {/* Mobile Filter */}
                        <div className="lg:hidden flex items-center justify-between mb-6">
                            <p className="text-muted-foreground text-sm">
                                {loading ? "Loading..." : `${products.length} products`}
                            </p>
                            <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
                                <SheetTrigger asChild>
                                    <Button variant="outline" size="sm" className="rounded-none gap-2" data-testid="mobile-filter-btn">
                                        <Filter className="h-4 w-4" />
                                        Filter
                                        {selectedCategory && (
                                            <Badge variant="secondary" className="ml-1 rounded-none">1</Badge>
                                        )}
                                    </Button>
                                </SheetTrigger>
                                <SheetContent side="bottom" className="bg-card border-border h-[60vh]">
                                    <SheetHeader className="mb-6">
                                        <SheetTitle className="font-heading">Filter by Category</SheetTitle>
                                    </SheetHeader>
                                    <CategoryFilters 
                                categories={categories}
                                selectedCategory={selectedCategory}
                                handleCategoryChange={handleCategoryChange}
                            />
                                </SheetContent>
                            </Sheet>
                        </div>
                        
                        {/* Active filter badge */}
                        {selectedCategory && (
                            <div className="mb-6 flex items-center gap-2">
                                <span className="text-muted-foreground text-sm">Filtered by:</span>
                                <Badge 
                                    variant="secondary" 
                                    className="rounded-none gap-1 cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                                    onClick={() => handleCategoryChange(null)}
                                >
                                    {currentCategory?.name}
                                    <X className="h-3 w-3" />
                                </Badge>
                            </div>
                        )}
                        
                        {/* Products Grid */}
                        {loading ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {[1, 2, 3, 4].map((i) => (
                                    <div key={i} className="bg-card border border-border h-96 animate-pulse" />
                                ))}
                            </div>
                        ) : products.length === 0 ? (
                            <div className="text-center py-16">
                                <p className="text-muted-foreground mb-4">No products found in this category.</p>
                                <Button variant="outline" onClick={() => handleCategoryChange(null)} className="rounded-none">
                                    View All Products
                                </Button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {products.map((product, index) => (
                                    <Card 
                                        key={product.id} 
                                        className="bg-card border border-border rounded-none group hover:border-primary/50 transition-colors overflow-hidden"
                                        data-testid={`product-card-${index}`}
                                    >
                                        <div className="aspect-[16/10] bg-secondary overflow-hidden">
                                            {product.images?.[0] ? (
                                                <img 
                                                    src={resolveImageUrl(product.images[0])} 
                                                    alt={product.display_title}
                                                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <span className="text-muted-foreground font-mono text-sm">No Image</span>
                                                </div>
                                            )}
                                        </div>
                                        <CardContent className="p-6">
                                            <div className="flex items-start justify-between gap-4 mb-2">
                                                <p className="font-mono text-xs text-muted-foreground">{product.internal_name}</p>
                                                {product.is_featured && (
                                                    <Badge className="rounded-none bg-primary/10 text-primary border-0">Featured</Badge>
                                                )}
                                            </div>
                                            <h3 className="font-heading font-bold text-xl mb-2">{product.display_title}</h3>
                                            <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{product.short_description}</p>
                                            
                                            {product.manufacturer && (
                                                <p className="text-muted-foreground/60 text-xs mb-4">
                                                    Manufactured by <span className="text-foreground">{product.manufacturer}</span> · Official Sales & Service by <span className="text-foreground">{product.sales_service_provider || "Dry Ice Bulls"}</span>
                                                </p>
                                            )}
                                            
                                            <div className="flex items-center justify-between pt-4 border-t border-border">
                                                <span className="font-mono text-primary font-bold">{product.price_text}</span>
                                                <Link to={`/products/${product.id}`}>
                                                    <Button variant="ghost" size="sm" className="rounded-none text-primary hover:bg-primary/10">
                                                        View Details
                                                        <ArrowRight className="ml-1 h-4 w-4" />
                                                    </Button>
                                                </Link>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductsPage;
